import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Input, FormGroup, Label, Form, Row, Col, Card, CardBody, InputGroup, InputGroupText } from "reactstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

const EditProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    id: id,
    name: "",
    code: "",
    unit: "",
    status: 1
  });

  const [errors, setErrors] = useState({});

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "45px",
      height: "45px",
      borderRadius: "8px",
      borderColor: state.isFocused ? "#2e4a3d" : (errors.unit ? "#dc3545" : "#dee2e6"),
      boxShadow: state.isFocused ? "0 0 0 0.1rem rgba(46, 74, 61, 0.25)" : "none",
      "&:hover": { borderColor: "#2e4a3d" },
    }),
    valueContainer: (provided) => ({ ...provided, height: "45px", padding: "0 12px" }),
    indicatorsContainer: (provided) => ({ ...provided, height: "45px" }),
    menuPortal: base => ({ ...base, zIndex: 9999 })
  };

  const unitOptions = [
    { value: "kg", label: "Kilogram (KG)" },
    { value: "quintal", label: "Quintal" },
    { value: "ton", label: "Ton" },
  ];

  const statusOptions = [
    { value: 1, label: "Active" },
    { value: 0, label: "Inactive" },
  ];

  // 1. Fetch existing product data
  useEffect(() => {
    const fetchProductData = async () => {
      dispatch(loadingTrue());
      try {
        const res = await Handler({ method: "GET", url: `/product/view.php?id=${id}` });
        if (res?.success) {
          const data = res.data?.product;
          setFormData({
            id: data.id,
            name: data.name,
            code: data.code,
            unit: data.unit,
            status: parseInt(data.status)
          });
          dispatch(loadingFalse());
        } else {
          toast.error(res?.message || "Failed to fetch product details");
          navigate("/products");
          dispatch(loadingFalse());
        }
      } catch (err) {
        toast.error("Error connecting to server");
      } finally {
        dispatch(loadingFalse());
      }
    };
    fetchProductData();
  }, [id, navigate, dispatch]);

  // 🔥 Strict English Validation Logic
  const validateForm = () => {
    let formErrors = {};
    let isValid = true;

    if (!formData.name || !formData.name.trim()) {
      formErrors.name = "Product name is required";
      isValid = false;
    }

    if (!formData.code || !formData.code.trim()) {
      formErrors.code = "Product code is required";
      isValid = false;
    }

    if (!formData.unit) {
      formErrors.unit = "Please select a valid unit";
      isValid = false;
    }

    setErrors(formErrors);
    return isValid;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Trigger local validation
    if (!validateForm()) {
      toast.error("Please correct the errors before updating.");
      return;
    }

    dispatch(loadingTrue());
    try {
      const res = await Handler({
        method: "POST",
        url: "/product/update.php",
        data: formData,
      });

      if (res?.success) {
        Swal.fire("Updated!", "Product updated successfully", "success");
        navigate("/products");
      } else {
        if (res?.errors) {
          setErrors(res.errors);
        } else {
          toast.error(res?.message || "Update failed");
        }
      }
    } catch (err) {
      Swal.fire("Error", "Server error occurred", "error");
    } finally {
      dispatch(loadingFalse());
    }
  };

  return (
    <div className="page-container p-1">
      <div className="page-header mb-4">
        <h6 className="text-muted">Inventory &gt; Products &gt; Update</h6>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h3 className="fw-bold">Update Product Details</h3>
            <p className="text-muted">Editing product ID: #{id}</p>
          </div>
          <Button color="secondary" outline onClick={() => navigate(-1)}>Back</Button>
        </div>
      </div>

      <Row className="justify-content-center">
        <Col lg={8}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
            <CardBody className="p-4">
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={12} className="mb-3">
                    <FormGroup>
                      <Label className="fw-bold">Product Name *</Label>
                      <Input
                        type="text"
                        name="name"
                        className={`form-control-lg ${errors.name ? "is-invalid" : ""}`}
                        value={formData.name}
                        onChange={handleChange}
                      />
                      {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                    </FormGroup>
                  </Col>

                  <Col md={6}>
                    <FormGroup>
                      <Label className="fw-bold">Product Code *</Label>
                      <InputGroup className={errors.code ? "is-invalid" : ""}>
                        <InputGroupText className="bg-light"><i className="bx bx-barcode"></i></InputGroupText>
                        <Input
                          type="text"
                          name="code"
                          className={errors.code ? "is-invalid" : ""}
                          value={formData.code}
                          onChange={handleChange}
                          style={{ height: "45px" }}
                        />
                      </InputGroup>
                      {errors.code && <div className="text-danger small mt-1">{errors.code}</div>}
                    </FormGroup>
                  </Col>

                  <Col md={6}>
                    <FormGroup>
                      <Label className="fw-bold">Default Unit *</Label>
                      <Select
                        options={unitOptions}
                        styles={customSelectStyles}
                        value={unitOptions.find(opt => opt.value === formData.unit)}
                        onChange={(opt) => {
                          setFormData({ ...formData, unit: opt.value });
                          setErrors({ ...errors, unit: "" });
                        }}
                      />
                      {errors.unit && <div className="text-danger small mt-1">{errors.unit}</div>}
                    </FormGroup>
                  </Col>

                  <Col md={12} className="mt-3">
                    <FormGroup>
                      <Label className="fw-bold">Inventory Status</Label>
                      <Select
                        options={statusOptions}
                        styles={customSelectStyles}
                        value={statusOptions.find(opt => opt.value === formData.status)}
                        onChange={(opt) => setFormData({ ...formData, status: opt.value })}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <div className="mt-4 pt-3 border-top d-flex justify-content-end gap-2">
                  <Button type="button" color="light" onClick={() => navigate(-1)}>Cancel</Button>
                  <Button type="submit" className="add-btn px-5">
                    <i className="bx bx-save me-1"></i> Update Product
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="border-0 shadow-sm bg-theme text-white" style={{ borderRadius: "15px" }}>
            <CardBody className="p-4 text-center">
              <i className="bx bx-refresh fs-1 mb-2 opacity-50"></i>
              <h5>Audit Info</h5>
              <p className="small opacity-75">All changes are tracked. Ensure the product code remains unique.</p>
              <hr className="my-3 opacity-25" />
              <div className="text-start small">
                <strong>ID:</strong> {formData.id}<br />
                <strong>Last Sync:</strong> {new Date().toLocaleTimeString()}
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default EditProduct;